"use client";

import { m, useInView } from "framer-motion";
import { useRef } from "react";
import Image from "next/image";
import SectionHeader from "../sectionHeader";

interface CaseStudyItem {
  image: { asset: { url: string }; alt?: string };
  logo?: { asset: { url: string }; alt?: string };
  title: string;
  text: string;
  metric?: string;
  slug?: string;
  imageScale?: number;
}

interface Props {
  data?: {
    title?: string;
    headline?: string;
    items: CaseStudyItem[];
  };
}

const itemVariants = {
  initial: { opacity: 0, y: 32 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      type: "spring",
    },
  },
};

export default function CaseStudiesSection({ data }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <m.section
      ref={ref}
      className="section"
      transition={{ staggerChildren: 0.1, delayChildren: 0.2 }}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
    >
      <div className="container mx-auto flex flex-col gap-16">
        <SectionHeader title={data?.title} headline={data?.headline} />

        {data?.items?.map((item, index) => {
          const imageFirst = index % 2 === 0;
          const imageAlt = item.image.alt ?? `${item.title} case study`;
          const logoAlt = item.logo?.alt ?? `${item.title} logo`;

          const imageBlock = (
            <div className="bg-surface aspect-[4/3] overflow-hidden">
              {item.image?.asset?.url && (
                <Image
                  src={item.image.asset.url}
                  alt={imageAlt}
                  width={800}
                  height={600}
                  sizes="(min-width: 768px) 50vw, 100vw"
                  priority={index === 0}
                  className="object-cover object-top w-full h-full"
                  style={item.imageScale ? { transform: `scale(${item.imageScale})`, transformOrigin: "top" } : undefined}
                />
              )}
            </div>
          );

          const textBlock = (
            <div className="frame flex flex-col justify-center items-start gap-6 p-6 lg:p-8">
              {item.logo && (
                <Image
                  src={item.logo.asset.url}
                  alt={logoAlt}
                  width={120}
                  height={40}
                  className="object-contain h-10 w-auto max-w-[160px]"
                />
              )}
              <h3 className="h3">{item.title}</h3>
              <p className="text-base leading-relaxed">{item.text}</p>
              {item.metric && (
                <span className="h4 text-success">{item.metric}</span>
              )}
            </div>
          );

          return (
            <m.div
              key={index}
              variants={itemVariants}
              className="grid md:grid-cols-2 gap-4"
            >
              {imageFirst ? (
                <>
                  {imageBlock}
                  {textBlock}
                </>
              ) : (
                <>
                  {textBlock}
                  {imageBlock}
                </>
              )}
            </m.div>
          );
        })}
      </div>
    </m.section>
  );
}
